import { Box, Link, styled, Tooltip } from '@material-ui/core';
import { Customer } from 'Services/MongoRealm/Types';
import { Product } from '@setvi/shared/interfaces';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import {
  getProductStock,
  getProductStockByCustomer
} from 'pages/products/utils';
import { Variant } from '@material-ui/core/styles/createTypography';
import { useState, CSSProperties } from 'react';
import { getRealtimeInventoryQuery } from '@setvi/shared/services';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '@material-ui/lab';

export enum StockColor {
  Green = '#23A566',
  Red = '#FA2020'
}

export const getStock = (
  inventory: Product['inventory'],
  selectedCustomer?: Customer
) => {
  const productStock = getProductStock(inventory);
  const stockForCustomer = getProductStockByCustomer(
    inventory,
    selectedCustomer
  );
  return selectedCustomer ? stockForCustomer : productStock;
};

const StyledLink = styled(Link)({
  wordBreak: 'break-word',
  lineHeight: '24px',
  textDecoration: 'none !important',
  display: 'flex',
  alignItems: 'center',
  gap: 2
});

interface StockProps {
  inventory: Product['inventory'];
  isDropship: boolean;
  /**
   * @param productId - The product id to fetch the realtime inventory
   * @description This is used to fetch the realtime inventory for the product
   */
  productId?: string;
  variant?: Variant;
  selectedCustomer?: Customer;
  stockInfo?: Product['stockInfo'];
  style?: CSSProperties;
  onClick?: () => void;
}

export const Stock = ({
  inventory,
  isDropship,
  productId,
  variant = 'body1',
  style = {},
  selectedCustomer,
  stockInfo,
  onClick
}: StockProps) => {
  const [shouldFetchRealtime, setShouldFetchRealtime] = useState(false);

  const { data: realtimeInventory, isFetching: realtimeInventoryFetching } =
    useQuery({
      ...getRealtimeInventoryQuery({ productId }),
      enabled: shouldFetchRealtime && !!productId
    });

  const inventoryToUse = realtimeInventory || inventory;

  const productStock = getProductStock(inventoryToUse);

  const stockForCustomer = getProductStockByCustomer(
    inventoryToUse,
    selectedCustomer
  );

  const stock = selectedCustomer ? stockForCustomer : productStock;
  const inStock = stock > 0;

  if (realtimeInventoryFetching) return <Skeleton width="90px" height="32px" />;

  if (stock === 0 && stockInfo?.allWarehouses === false) return '-';

  if (isDropship)
    return (
      <Tooltip
        arrow
        title="It is not stocked in the distributor's warehouse; instead, it is shipped directly from the vendor.">
        <Link
          variant={variant}
          underline="always"
          color="primary"
          style={{ cursor: onClick ? 'pointer' : 'default', ...style }}
          onClick={e => {
            e.stopPropagation();
            onClick?.();
          }}>
          Drop ship
        </Link>
      </Tooltip>
    );

  return (
    <Tooltip
      arrow
      placement="top"
      style={{ width: 'fit-content' }}
      title={
        <Box p={1}>
          <Box pb={1}>
            <b>Available in Warehouse</b>
          </Box>
          <ul
            style={{
              padding: 0,
              margin: 0,
              listStyle: 'none'
            }}>
            {inventory?.length === 0 && (
              <li>No stock information available. </li>
            )}
            {inventory?.map(({ warehouse, availableOnHand }) => (
              <li key={warehouse?._id}>
                {warehouse?.name} - In stock: {availableOnHand}
              </li>
            ))}
          </ul>
        </Box>
      }>
      <StyledLink
        variant={variant}
        style={{
          wordBreak: 'break-word',
          color: inStock ? StockColor.Green : StockColor.Red,
          cursor: !shouldFetchRealtime ? 'pointer' : 'default',
          ...style
        }}
        onClick={e => {
          e.stopPropagation();
          setShouldFetchRealtime(true);
          onClick?.();
        }}>
        {inStock ? (
          <>
            In stock: {stock}
            <InfoOutlinedIcon style={{ fontSize: 14 }} />
          </>
        ) : (
          'Out of stock'
        )}
      </StyledLink>
    </Tooltip>
  );
};
